/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Plus, Loader2, Check, ChevronsUpDown, Search } from "lucide-react"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import PageMainContainer from "@/components/Templates/PageMainContainer"
import {
  getLectures,
  getCategories,
  createLecture,
  updateLecture,
  deleteLecture,
} from "@/services/lecturesService"
import { Lecture } from "@/types/lectures"
import { useLoading } from "@/hooks/useLoading"
import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/use-toast"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { PalestraBottomSheet } from "@/modules/Lectures/components/PalestraBottomSheet"
import { LectureCard } from "@/modules/Lectures/components/LectureCard"
import { LecturesFilter } from "@/modules/Lectures/components/LecturesFilter"
import { Skeleton } from "@/components/ui/skeleton"
import { format, subMonths } from "date-fns"
import { useAuth } from "@/modules/Auth/context/AuthContext"

// Paleta fixa: a cor é atribuída por categoria (não por posição na lista),
// para que a mesma categoria tenha sempre a mesma cor em todos os agrupamentos.
const CATEGORY_COLORS = [
  {
    accent: "text-orange-500",
    border: "border-orange-500/20",
    bg: "from-orange-500/[0.04]",
    glow: "bg-orange-500/10",
    fill: "bg-orange-500",
  },
  {
    accent: "text-blue-500",
    border: "border-blue-500/20",
    bg: "from-blue-500/[0.04]",
    glow: "bg-blue-500/10",
    fill: "bg-blue-500",
  },
  {
    accent: "text-emerald-500",
    border: "border-emerald-500/20",
    bg: "from-emerald-500/[0.04]",
    glow: "bg-emerald-500/10",
    fill: "bg-emerald-500",
  },
  {
    accent: "text-purple-500",
    border: "border-purple-500/20",
    bg: "from-purple-500/[0.04]",
    glow: "bg-purple-500/10",
    fill: "bg-purple-500",
  },
  {
    accent: "text-rose-500",
    border: "border-rose-500/20",
    bg: "from-rose-500/[0.04]",
    glow: "bg-rose-500/10",
    fill: "bg-rose-500",
  },
  {
    accent: "text-cyan-400",
    border: "border-cyan-400/20",
    bg: "from-cyan-400/[0.04]",
    glow: "bg-cyan-400/10",
    fill: "bg-cyan-400",
  },
  {
    accent: "text-amber-400",
    border: "border-amber-400/20",
    bg: "from-amber-400/[0.04]",
    glow: "bg-amber-400/10",
    fill: "bg-amber-400",
  },
  {
    accent: "text-indigo-400",
    border: "border-indigo-400/20",
    bg: "from-indigo-400/[0.04]",
    glow: "bg-indigo-400/10",
    fill: "bg-indigo-400",
  },
]

const PERIOD_GROUPS = [
  { key: "3m", label: "Últimos 3 meses", months: 3 },
  { key: "6m", label: "Últimos 6 meses", months: 6 },
  { key: "9m", label: "Últimos 9 meses", months: 9 },
  { key: "12m", label: "Últimos 12 meses", months: 12 },
  { key: "older", label: "Mais antigos", months: null },
] as const

// Datas ausentes/inválidas viram epoch: caem no fim da lista e no grupo "Mais antigos",
// sem quebrar a consistência do comparador de ordenação
function toTimestamp(value?: string): number {
  const time = new Date(value ?? "").getTime()
  return Number.isNaN(time) ? 0 : time
}

function extractYoutubeVideoId(url: string): string | null {
  if (!url) return null
  const patterns = [
    /youtu\.be\/([a-zA-Z0-9_-]+)/,
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

export default function LecturesPage() {
  const { hideLoading, showLoading } = useLoading()
  const { toast } = useToast()
  const { user } = useAuth()

  const ITEMS_PER_PAGE = 48

  // --- ESTADOS GERAIS ---
  const [lectures, setLectures] = useState<Lecture[]>([])
  const [allCategories, setAllCategories] = useState<string[]>([])
  const [categoryOrder, setCategoryOrder] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [isLoading, setIsLoading] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [selectedPalestra, setSelectedPalestra] = useState<Lecture | null>(null)

  // --- ESTADOS DA MODAL (SHEET) ---
  const [editingLecture, setEditingLecture] = useState<Partial<Lecture> | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // --- CAMPOS DO FORMULÁRIO ---
  const [nomeInput, setNomeInput] = useState("")
  const [descricaoInput, setDescricaoInput] = useState("")
  const [linkInput, setLinkInput] = useState("")
  const [dateInput, setDateInput] = useState("")

  // --- ESTADOS DOS SELECTS MANUAIS ---
  const [categoriaInput, setCategoriaInput] = useState("")
  const [isCategoriaOpen, setIsCategoriaOpen] = useState(false)
  const [searchCategoria, setSearchCategoria] = useState("")
  const categoriaRef = useRef<HTMLDivElement>(null)

  // --- DEBOUNCE BUSCA ---
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 500)
    return () => clearTimeout(handler)
  }, [searchTerm])

  const init = useCallback(async () => {
    setIsLoading(true)
    setCurrentPage(1)
    try {
      const [catRes, lectureRes] = await Promise.all([
        getCategories(),
        getLectures({
          page: 1,
          limit: ITEMS_PER_PAGE,
          search: debouncedSearch,
          categoria: categoryFilter !== "all" ? categoryFilter : undefined,
        }),
      ])
      const cats = catRes.data.data.map((item: any) => item.categoria).filter(Boolean)
      setCategoryOrder(Array.from(new Set<string>(cats)))
      setAllCategories(Array.from(new Set<string>(cats.filter((c: string) => c !== "SALA DE GUERRA"))))
      const data = lectureRes.data.data || []
      setLectures(data)
      setHasMore(lectureRes.data.meta.hasMore ?? lectureRes.data.meta.total > ITEMS_PER_PAGE)
    } catch (error) {
      toast({ variant: "destructive", title: "Erro ao carregar dados" })
    } finally {
      setIsLoading(false)
      hideLoading()
    }
  }, [debouncedSearch, categoryFilter, hideLoading, toast])

  useEffect(() => {
    init()
  }, [init])

  const handleDateChange = (e: any) => {
    let val = e.target.value.replace(/\D/g, "").slice(0, 8)
    if (val.length >= 5) val = val.replace(/^(\d{2})(\d{2})(\d{0,4}).*/, "$1/$2/$3")
    else if (val.length >= 3) val = val.replace(/^(\d{2})(\d{0,2}).*/, "$1/$2")
    setDateInput(val)
  }

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (categoriaRef.current && !categoriaRef.current.contains(e.target as Node))
        setIsCategoriaOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  useEffect(() => {
    if (editingLecture) {
      setNomeInput(editingLecture.nome || "")
      setDescricaoInput(editingLecture.descricao || "")
      setLinkInput(editingLecture.link || "")
      setCategoriaInput(editingLecture.categoria || "")
      setDateInput(editingLecture.data ? format(new Date(editingLecture.data), "dd/MM/yyyy") : "")
    } else {
      setNomeInput("")
      setDescricaoInput("")
      setLinkInput("")
      setCategoriaInput("")
      setDateInput("")
    }
  }, [editingLecture])

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      const isEditing = !!editingLecture?.id
      const [d, m, y] = dateInput.split("/")
      const payload = {
        nome: nomeInput,
        descricao: descricaoInput,
        link: linkInput,
        categoria: categoriaInput,
        data: `${y}-${m}-${d}`,
        id_youtube: extractYoutubeVideoId(linkInput),
      }
      isEditing ? await updateLecture(editingLecture.id!, payload) : await createLecture(payload)
      toast({ title: "Sucesso!", description: "Palestra salva com sucesso." })
      setEditingLecture(null)
      init()
    } catch (err: any) {
      toast({ variant: "destructive", title: err.response?.data?.message || "Erro ao salvar" })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (item: Lecture) => {
    showLoading()
    try {
      await deleteLecture(item.id)
      toast({ title: "Palestra excluída com sucesso!" })
      init()
    } catch (error) {
      toast({ variant: "destructive", title: "Erro ao excluir palestra." })
    } finally {
      hideLoading()
    }
  }

  const loadMore = async () => {
    if (isLoadingMore || !hasMore) return
    setIsLoadingMore(true)
    try {
      const nextPage = currentPage + 1
      const res = await getLectures({
        page: nextPage,
        limit: ITEMS_PER_PAGE,
        search: debouncedSearch,
        categoria: categoryFilter !== "all" ? categoryFilter : undefined,
      })
      setLectures((prev) => [...prev, ...(res.data.data || [])])
      setCurrentPage(nextPage)
      setHasMore(res.data.meta.hasMore ?? nextPage * ITEMS_PER_PAGE < res.data.meta.total)
    } catch {
      toast({ variant: "destructive", title: "Erro ao carregar mais palestras." })
    } finally {
      setIsLoadingMore(false)
    }
  }

  // Cor fixa por categoria: a posição na lista ordenada define o índice na paleta,
  // então a mesma categoria mantém a cor em qualquer agrupamento ou página
  const categoryThemes = useMemo(() => {
    const known = Array.from(
      new Set([...categoryOrder, ...lectures.map((item) => item.categoria)].filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, "pt-BR"))

    return new Map(known.map((cat, idx) => [cat, CATEGORY_COLORS[idx % CATEGORY_COLORS.length]]))
  }, [categoryOrder, lectures])

  const getCategoryTheme = useCallback(
    (categoria?: string) => categoryThemes.get(categoria ?? "") ?? CATEGORY_COLORS[0],
    [categoryThemes]
  )

  // Agrupamento trimestral: os itens chegam ordenados por data DESC, então as faixas
  // são contíguas e o "Carregar mais" apenas estende o último grupo visível.
  const lectureGroups = useMemo(() => {
    const now = new Date()
    const thresholds = PERIOD_GROUPS.filter((group) => group.months !== null).map((group) => ({
      key: group.key,
      limit: subMonths(now, group.months as number).getTime(),
    }))

    const buckets = new Map<string, { item: Lecture; index: number }[]>()

    ;[...lectures]
      .sort((a, b) => toTimestamp(b.data) - toTimestamp(a.data))
      .forEach((item, index) => {
        const time = toTimestamp(item.data)
        const key = thresholds.find((threshold) => time >= threshold.limit)?.key ?? "older"
        const bucket = buckets.get(key)
        if (bucket) bucket.push({ item, index })
        else buckets.set(key, [{ item, index }])
      })

    return PERIOD_GROUPS.map((group) => ({
      key: group.key,
      label: group.label,
      items: buckets.get(group.key) ?? [],
    })).filter((group) => group.items.length > 0)
  }, [lectures])

  const isFormValid = useMemo(
    () =>
      nomeInput.trim() !== "" &&
      categoriaInput.trim() !== "" &&
      linkInput.trim() !== "" &&
      dateInput.length === 10,
    [nomeInput, categoriaInput, linkInput, dateInput]
  )

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-zinc-950 pb-24 text-zinc-100 md:pb-32">
      {/* Header Responsivo: Empilha no mobile e afasta no desktop */}
      <div className="flex flex-col items-start justify-between gap-6 px-4 md:flex-row md:items-center md:px-10">
        <PageHeader
          title="Palestras"
          icon="chalkboard-user"
        />
        {user.gestaoApp && (
          <Button
            onClick={() => setEditingLecture({})}
            className="h-12 w-full rounded-xl bg-orange-600 px-6 text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition-all hover:scale-[1.02] hover:bg-orange-600 active:scale-95 md:h-14 md:w-auto md:rounded-2xl md:px-8 md:text-xs"
          >
            <Plus
              size={18}
              className="mr-2"
            />
            Nova Palestra
          </Button>
        )}
      </div>

      <PageMainContainer>
        <div className="mb-8 md:mb-12">
          <LecturesFilter
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
            categories={allCategories}
          />
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-[280px] w-full rounded-3xl bg-zinc-900" />
            ))}
          </div>
        ) : (
          <>
            {lectureGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 rounded-3xl border border-dashed border-white/10 py-20 text-center">
                <p className="text-[11px] font-black uppercase tracking-widest text-zinc-400">
                  Nenhuma palestra encontrada
                </p>
                <p className="text-xs text-zinc-600">Ajuste a busca ou o filtro de categoria.</p>
              </div>
            ) : (
              <div className="space-y-10 md:space-y-14">
                {lectureGroups.map((group, groupIdx) => {
                  // O último grupo pode estar incompleto enquanto houver páginas por carregar
                  const isPartial = hasMore && groupIdx === lectureGroups.length - 1

                  return (
                    <section key={group.key}>
                      <div className="mb-4 flex items-center gap-4 md:mb-6">
                        <h2 className="whitespace-nowrap text-[10px] font-black uppercase tracking-widest text-orange-500 md:text-xs">
                          {group.label}
                        </h2>
                        {!isPartial && (
                          <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                            {group.items.length} {group.items.length === 1 ? "palestra" : "palestras"}
                          </span>
                        )}
                        <div className="h-px flex-1 bg-white/10" />
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                        {group.items.map(({ item, index }) => (
                          <LectureCard
                            key={item.id}
                            item={item}
                            index={index}
                            theme={getCategoryTheme(item.categoria)}
                            onSelect={setSelectedPalestra}
                            onEdit={(item) => setEditingLecture(item)}
                            onDelete={(item) => handleDelete(item)}
                          />
                        ))}
                      </div>
                    </section>
                  )
                })}
              </div>
            )}

            {hasMore && (
              <div className="mt-8 flex justify-center md:mt-12">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="h-12 w-full rounded-full bg-transparent px-6 text-[10px] font-black uppercase tracking-widest text-orange-500 transition-all hover:bg-orange-500 hover:text-black sm:w-auto md:h-14 md:px-10 md:text-[11px]"
                >
                  {isLoadingMore ? <Loader2 className="mr-2 animate-spin" /> : "Carregar mais palestras"}
                </Button>
              </div>
            )}
          </>
        )}
      </PageMainContainer>

      {/* Sheet Otimizado: Scroll interno para evitar quebra no mobile */}
      <Sheet
        open={!!editingLecture}
        onOpenChange={() => setEditingLecture(null)}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col border-zinc-800 bg-zinc-950 p-0 text-white sm:max-w-md"
        >
          <form
            onSubmit={handleFormSubmit}
            className="flex h-full flex-col"
          >
            <SheetHeader className="border-b border-white/5 p-6 text-left text-white md:p-8 md:pb-6">
              <SheetTitle className="text-xl font-black uppercase italic md:text-2xl">
                {editingLecture?.id ? "Editar" : "Nova"} Palestra
              </SheetTitle>
              <SheetDescription className="text-[10px] font-bold uppercase italic tracking-widest text-orange-500">
                Detalhes da Palestra
              </SheetDescription>
            </SheetHeader>

            <div className="custom-scrollbar flex-1 space-y-5 overflow-y-auto p-6 md:space-y-6 md:p-8">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                  Nome
                </Label>
                <Input
                  className="h-12 border-zinc-800 bg-zinc-900 text-white md:h-14"
                  value={nomeInput}
                  onChange={(e) => setNomeInput(e.target.value)}
                />
              </div>

              <div
                className="space-y-2"
                ref={categoriaRef}
              >
                <Label className="text-[10px] font-black uppercase text-zinc-500">Categoria</Label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsCategoriaOpen(!isCategoriaOpen)}
                    className="flex h-12 w-full items-center justify-between rounded-md border border-zinc-800 bg-zinc-900 px-4 text-xs text-zinc-300 md:h-14"
                  >
                    <span className="truncate">{categoriaInput || "Selecione..."}</span>
                    <ChevronsUpDown
                      size={16}
                      className="opacity-50"
                    />
                  </button>
                  <AnimatePresence>
                    {isCategoriaOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute left-0 top-[55px] z-[100] w-full rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl md:top-[60px]"
                      >
                        <div className="flex items-center border-b border-white/5 p-3">
                          <Search
                            size={14}
                            className="mr-2 text-zinc-500"
                          />
                          <input
                            className="w-full bg-transparent text-sm outline-none"
                            placeholder="Buscar ou criar categoria..."
                            value={searchCategoria}
                            onChange={(e) => setSearchCategoria(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div className="custom-scrollbar max-h-[180px] overflow-y-auto p-1 md:max-h-[200px]">
                          {allCategories
                            .filter((c) => c.toLowerCase().includes(searchCategoria.toLowerCase()))
                            .map((cat) => (
                              <button
                                key={cat}
                                type="button"
                                onClick={() => {
                                  setCategoriaInput(cat)
                                  setIsCategoriaOpen(false)
                                }}
                                className="flex w-full items-center justify-between rounded-lg p-3 text-left text-xs text-zinc-400 hover:bg-white/5"
                              >
                                {cat}
                                {categoriaInput === cat && (
                                  <Check
                                    size={14}
                                    className="text-orange-500"
                                  />
                                )}
                              </button>
                            ))}
                          {searchCategoria &&
                            !allCategories.some(
                              (c) => c.toLowerCase() === searchCategoria.toLowerCase()
                            ) && (
                              <button
                                type="button"
                                onClick={() => {
                                  setCategoriaInput(searchCategoria)
                                  setIsCategoriaOpen(false)
                                }}
                                className="flex w-full p-3 text-xs font-bold italic text-orange-500 underline"
                              >
                                Criar nova categoria "{searchCategoria}"
                              </button>
                            )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                  Link do YouTube
                </Label>
                <Input
                  className="h-12 border-zinc-800 bg-zinc-900 font-mono text-xs text-white md:h-14 md:text-sm"
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                  Data
                </Label>
                <Input
                  placeholder="DD/MM/YYYY"
                  className="h-12 border-zinc-800 bg-zinc-900 text-center font-mono text-white md:h-14"
                  value={dateInput}
                  onChange={handleDateChange}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                  Descrição (Opcional)
                </Label>
                <textarea
                  className="h-24 w-full rounded-md border border-zinc-800 bg-zinc-900 p-4 text-sm text-white outline-none focus:ring-1 focus:ring-orange-500"
                  value={descricaoInput}
                  onChange={(e) => setDescricaoInput(e.target.value)}
                />
              </div>
            </div>

            <SheetFooter className="border-t border-white/5 p-6 md:p-8">
              <Button
                type="submit"
                disabled={isSaving || !isFormValid}
                className="h-14 w-full bg-orange-600 font-black uppercase tracking-widest shadow-lg hover:bg-orange-500 md:h-16"
              >
                {isSaving ? <Loader2 className="animate-spin" /> : "Salvar Palestra"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <PalestraBottomSheet
        open={!!selectedPalestra}
        onOpenChange={(o) => !o && setSelectedPalestra(null)}
        title={selectedPalestra?.nome}
        description={selectedPalestra?.descricao}
        videoUrl={selectedPalestra?.link}
      />
    </div>
  )
}

